import type {
  DataSyncTask,
  UpdateDataSyncTaskRequest,
} from './validations/types';

import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { vestigasApiNoConversion } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { omitUnsupportedDeactivationConfig } from './deactivationConfig';
import { queryKeysDataSyncJob } from './queryKeys';
import {
  parseDataSyncResponse,
  rawEntityResponseSchema,
} from './responseShapes';
import {
  fromApiKeepingUserKeys,
  toApiKeepingUserKeys,
} from './userKeyedFields';
import { createDataSyncTaskSchema } from './validations';

type UpdateTaskParams = {
  taskId: string;
  data: UpdateDataSyncTaskRequest;
};

/**
 * Updates an existing data sync task.
 */
export const updateDataSyncTask = async ({
  taskId,
  data,
}: UpdateTaskParams): Promise<DataSyncTask> => {
  // Decode: Transform UI format (array) → API format (object), then drop
  // deactivationConfig for entity types the backend does not apply it to.
  const validatedData = omitUnsupportedDeactivationConfig(
    createDataSyncTaskSchema.decode(data),
  );

  const rawResponse = await vestigasApiNoConversion
    .put(ENDPOINT.DATA_SYNC.UPDATE_TASK(taskId), {
      json: toApiKeepingUserKeys(validatedData),
    })
    .json<unknown>();

  return fromApiKeepingUserKeys(
    parseDataSyncResponse(rawEntityResponseSchema, rawResponse, 'task update'),
  );
};

/**
 * React Query mutation hook for updating a data sync task.
 * Automatically invalidates relevant queries on success.
 *
 * @param options - Additional mutation options
 */
export const useMutationUpdateTask = (
  options?: Omit<
    UseMutationOptions<DataSyncTask, Error, UpdateTaskParams>,
    'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    ...options,
    mutationFn: updateDataSyncTask,
    async onSuccess() {
      // Invalidate tasks list and specific task detail
      await queryClient.invalidateQueries({
        queryKey: queryKeysDataSyncJob.base(),
      });

      ToastService.success('Sync-Task erfolgreich aktualisiert');
    },
  });

  return mutation;
};
